/**
 * React Query hooks for the ADR-141 contact directory.
 *
 * Key factories for BOTH scopes live here (`contactKeys`, `contactTagKeys`)
 * so the cross-scope invalidation below stays one-directional:
 * `useContactTags.ts` imports from this module, never the reverse.
 *
 * Invalidation semantics:
 *   - contact writes invalidate the contacts scope;
 *   - contact writes that touch tag assignments ALSO invalidate the tags
 *     scope (each tag row carries a `contact_count` annotation);
 *   - tag writes (see `useContactTags.ts`) invalidate both scopes — the
 *     contact list renders `tag_name` chips that a rename/delete changes.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addContactDestination,
  assignContactTag,
  createContact,
  deleteContactDestination,
  getContacts,
  patchContact,
  patchContactDestination,
  unassignContactTag,
  type Contact,
  type ContactPatchPayload,
  type ContactWritePayload,
} from '../../services/contacts.service';

export const contactKeys = {
  all: ['contacts'] as const,
  list: () => [...contactKeys.all, 'list'] as const,
};

export const contactTagKeys = {
  all: ['contactTags'] as const,
  list: () => [...contactTagKeys.all, 'list'] as const,
};

export function useContacts() {
  return useQuery({
    queryKey: contactKeys.list(),
    queryFn: getContacts,
  });
}

/** Row-level PATCH — deactivate / reactivate from the list view. */
export function usePatchContact() {
  const queryClient = useQueryClient();
  return useMutation<
    Contact,
    Error,
    { contactId: number; body: ContactPatchPayload }
  >({
    mutationFn: ({ contactId, body }) => patchContact(contactId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

// =============================================================================
// Drawer save orchestration
// =============================================================================

/**
 * One editable destination row in the drawer. `id` present = existing row
 * (PATCH/keep); absent = new row (POST). Priority is positional — the
 * caller's array order is persisted as `priority: index`.
 */
export interface DestinationDraft {
  id?: number;
  channel_code: string;
  address: string;
  enabled: boolean;
}

export interface SaveContactInput {
  contact: ContactWritePayload;
  destinations: DestinationDraft[];
  tagIds: number[];
  /** The persisted contact when editing; null/undefined when creating. */
  original?: Contact | null;
}

/**
 * The destination + tag sub-resources have row-level endpoints (no nested
 * writes on the contact), so a drawer save is an orchestrated sequence:
 * upsert the contact, then diff destinations (delete removed → add new /
 * patch changed, positional priority), then diff tag assignments.
 * Sequential on purpose — bounded by drawer-sized row counts.
 */
export async function persistContactWithRelated(
  input: SaveContactInput,
): Promise<Contact> {
  const original = input.original ?? null;
  let saved: Contact;
  if (original === null) {
    saved = await createContact(input.contact);
  } else {
    saved = await patchContact(original.id, input.contact);
  }
  const contactId = saved.id;

  // --- Destinations: delete removed, then add/patch in positional order.
  const originalDestinations = original?.destinations ?? [];
  const keptIds = new Set(
    input.destinations
      .map((draft) => draft.id)
      .filter((id): id is number => id != null),
  );
  for (const destination of originalDestinations) {
    if (!keptIds.has(destination.id)) {
      await deleteContactDestination(contactId, destination.id);
    }
  }
  for (const [index, draft] of input.destinations.entries()) {
    const payload = {
      channel_code: draft.channel_code,
      address: draft.address.trim(),
      priority: index,
      enabled: draft.enabled,
    };
    if (draft.id == null) {
      await addContactDestination(contactId, payload);
      continue;
    }
    const existing = originalDestinations.find((d) => d.id === draft.id);
    const changed =
      existing === undefined ||
      existing.channel_code !== payload.channel_code ||
      (existing.address ?? '') !== payload.address ||
      (existing.priority ?? 0) !== payload.priority ||
      (existing.enabled ?? true) !== payload.enabled;
    if (changed) {
      await patchContactDestination(contactId, draft.id, payload);
    }
  }

  // --- Tags: assign added, unassign removed.
  const originalTagIds = new Set(
    (original?.tags ?? []).map((assignment) => assignment.tag),
  );
  for (const tagId of input.tagIds) {
    if (!originalTagIds.has(tagId)) {
      await assignContactTag(contactId, tagId);
    }
  }
  for (const tagId of originalTagIds) {
    if (!input.tagIds.includes(tagId)) {
      await unassignContactTag(contactId, tagId);
    }
  }

  return saved;
}

export function useSaveContact() {
  const queryClient = useQueryClient();
  return useMutation<Contact, Error, SaveContactInput>({
    mutationFn: (input) => persistContactWithRelated(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
      // Tag assignment changes move the per-tag contact_count.
      queryClient.invalidateQueries({ queryKey: contactTagKeys.all });
    },
  });
}
